import React, { useContext } from 'react';
import {
  Box,
  Typography,
  Menu,
  Divider,
  Switch,
  Avatar,
  Badge,
  styled,
  Tooltip
} from '@mui/material';
import {
  Work as WorkIcon,
  Email as EmailIcon,
  ManageAccounts as ManageAccountsIcon,
  DataUsage as DataUsageIcon,
  SettingsOutlined as SettingsOutlinedIcon,
  OpenInNew as OpenInNewIcon,
  Logout as LogoutIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useTheme } from '@emotion/react';
import { ColorContext } from '../AppTheme';
import { useActiveProject } from '../store/projectState';
import { useCookies } from 'react-cookie';
import { useMenu } from './base/useMenu';
import { useQueryClient } from 'react-query';
import { useScandiumMutation } from '../data-layer/utils';
import useLogout from '../hooks/useLogout';
import { toast } from 'react-toastify';
import { resetMixpanelData } from '../mixpanel.constants';

const ProfileAvatar = styled(Avatar)(({ theme }) => ({
  width: 40,
  height: 40,
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  fontWeight: 600,
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: theme.shadows[4]
  }
}));

const MenuItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1, 1.5),
  borderRadius: theme.shape.borderRadius,
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    transform: 'translateX(2px)'
  }
}));

const MenuLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.85rem',
  fontWeight: 500,
  color: theme.palette.text.secondary,
  marginLeft: theme.spacing(1.5)
}));

const MenuDivider = styled(Divider)(({ theme }) => ({
  margin: theme.spacing(1, 0),
  backgroundColor: theme.palette.divider
}));

export const CircularImage = ({ name = 'Scandium', enterprise = false, ...props }) => {
  const activeProject = useActiveProject();
  const { mode, setMode } = useContext(ColorContext);
  const [cookies] = useCookies([
    'token',
    'email',
    'name',
    'local_features',
    'company',
    'active_company',
    'role',
    'designation'
  ]);
  const logout = useLogout();
  const { triggerProps, menuProps } = useMenu();
  const queryClient = useQueryClient();
  const theme = useTheme();

  const showSettingsMenu =
    cookies?.active_company?.type === 'enterprise' &&
    (cookies?.role === 'owner' || cookies?.role === 'admin');

  const { mutateAsync: apiLogout } = useScandiumMutation('/auth/logout', {
    method: 'GET'
  });

  const handleLogout = async () => {
    try {
      await apiLogout();
      logout();
      queryClient.clear();
      resetMixpanelData();
    } catch (error) {
      toast.error('An error occurred, please try again');
    }
  };

  const initials = name
    .split(' ')
    .map((c) => c[0])
    .slice(0, 2)
    .join('');

  return (
    <Box>
      <Tooltip title="Account settings" arrow>
        <Badge
          overlap="circular"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          variant="dot"
          color="success"
          invisible={!enterprise}>
          <ProfileAvatar {...props} {...triggerProps}>
            {cookies?.active_company?.avatar && enterprise ? (
              <img
                src={cookies?.active_company?.avatar}
                alt="Enterprise avatar"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              initials
            )}
          </ProfileAvatar>
        </Badge>
      </Tooltip>

      <Menu
        id="account-menu"
        {...menuProps}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        PaperProps={{
          elevation: 4,
          sx: {
            width: 320,
            maxWidth: '100%',
            p: 1.5,
            mt: 1.5,
            borderRadius: '12px',
            overflow: 'visible',
            boxShadow: theme.shadows[10],
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0
            }
          }
        }}>
        <Box mb={2}>
          <Typography variant="h6" color="text.primary" fontWeight={600}>
            {name}
          </Typography>
          <Box display="flex" alignItems="center" mt={0.5}>
            <WorkIcon sx={{ color: 'text.secondary', mr: 1, fontSize: '1rem' }} />
            <Typography variant="body2" color="text.secondary">
              {cookies?.active_company?.name || cookies?.company?.name || 'No company'}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" mt={0.5}>
            <EmailIcon sx={{ color: 'text.secondary', mr: 1, fontSize: '1rem' }} />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
              {cookies?.email || 'No email'}
            </Typography>
          </Box>
        </Box>

        <MenuDivider />

        <MenuItem component={Link} to="/settings/profile" onClick={() => menuProps.onClose()}>
          <ManageAccountsIcon sx={{ color: 'text.secondary' }} />
          <MenuLabel>Profile Settings</MenuLabel>
        </MenuItem>

        <MenuItem
          component={Link}
          to={`projects/${activeProject?.id}/accountUsage`}
          onClick={() => menuProps.onClose()}>
          <DataUsageIcon sx={{ color: 'text.secondary' }} />
          <MenuLabel>Account Usage</MenuLabel>
        </MenuItem>

        {showSettingsMenu && (
          <>
            <MenuDivider />
            <MenuItem component={Link} to="/organizations" onClick={() => menuProps.onClose()}>
              <SettingsOutlinedIcon sx={{ color: 'text.secondary' }} />
              <MenuLabel>Organization Settings</MenuLabel>
            </MenuItem>
          </>
        )}

        <MenuDivider />

        <MenuItem
          component={Link}
          target="_blank"
          to={`/projects/${activeProject?.id}/mailbox`}
          onClick={() => menuProps.onClose()}>
          <OpenInNewIcon sx={{ color: 'text.secondary' }} />
          <MenuLabel>Open Mailbox</MenuLabel>
        </MenuItem>

        <MenuDivider />

        <MenuItem>
          {mode === 'dark' ? (
            <DarkModeIcon sx={{ color: 'text.secondary' }} />
          ) : (
            <LightModeIcon sx={{ color: 'text.secondary' }} />
          )}
          <MenuLabel sx={{ flexGrow: 1 }}>
            {mode === 'light' ? 'Dark Mode' : 'Light Mode'}
          </MenuLabel>
          <Switch
            checked={mode !== 'light'}
            size="small"
            onChange={() => {
              setMode((prev) => {
                const newMode = prev === 'light' ? 'dark' : 'light';
                localStorage.setItem('scandium-ui-mode', newMode);
                return newMode;
              });
            }}
          />
        </MenuItem>

        <MenuDivider />

        <MenuItem
          onClick={() => {
            handleLogout();
            menuProps.onClose();
          }}
          sx={{
            cursor: 'pointer'
            // '&:hover': {
            //   backgroundColor: theme.palette.error.light + '!important'
            // }
          }}>
          <LogoutIcon color="error" />
          <MenuLabel sx={{ color: 'error.main' }}>Logout</MenuLabel>
        </MenuItem>
      </Menu>
    </Box>
  );
};
